import React from 'react';
import { Popconfirm as AntPopconfirm, PopconfirmProps as AntPopconfirmProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface PopconfirmProps extends AntPopconfirmProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  popconfirm: {
    '& .ant-popover-inner': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      padding: theme.spacing[4],
      borderRadius: theme.radius.sm,
      boxShadow: theme.shadow.lg,
    },
    '& .ant-popover-inner-content': {
      color: theme.color.semantic.text.primary,
    },
    '& .ant-popover-message-title': {
      color: theme.color.semantic.text.primary,
    },
    '& .ant-popover-arrow-content': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
    },
    // Button container
    '& .ant-popconfirm-buttons': {
      display: 'flex',
      gap: theme.spacing[2],
      marginTop: theme.spacing[3],
    },
    // OK Button (Primary)
    '& .ant-popover-buttons .ant-btn-primary, & .ant-popconfirm-buttons .ant-btn-primary': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      color: `${theme.color.semantic.text.inverse} !important`,
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      fontWeight: `${theme.typography.fontWeight.medium} !important`,
      borderWidth: `${theme.border.width.sm} !important`,
      borderStyle: 'solid !important',
      borderRadius: `${theme.radius.sm} !important`,
      fontSize: `${theme.typography.fontSize.sm} !important`,
      padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
      '&:hover:not(:disabled)': {
        backgroundColor: `${theme.color.semantic.brand.default.secondary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
        borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      },
      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.brand.default.strong} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
        borderColor: `${theme.color.semantic.brand.default.strong} !important`,
      },
      '&:disabled, &.ant-btn-disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.border.subtle} !important`,
      },
    },
    // OK Button when danger (Primary Danger)
    '& .ant-popover-buttons .ant-btn-primary.ant-btn-dangerous, & .ant-popconfirm-buttons .ant-btn-primary.ant-btn-dangerous': {
      backgroundColor: `${theme.color.semantic.brand.danger.primary} !important`,
      color: `${theme.color.semantic.brand.danger.inverse} !important`,
      borderColor: `${theme.color.semantic.brand.danger.primary} !important`,
      '&:hover:not(:disabled)': {
        backgroundColor: `${theme.color.semantic.brand.danger.muted} !important`,
        color: `${theme.color.semantic.brand.danger.inverse} !important`,
        borderColor: `${theme.color.semantic.brand.danger.muted} !important`,
      },
      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.brand.danger.primary} !important`,
        color: `${theme.color.semantic.brand.danger.inverse} !important`,
        borderColor: `${theme.color.semantic.brand.danger.primary} !important`,
      },
    },
    // Cancel Button (Secondary)
    '& .ant-popover-buttons .ant-btn:not(.ant-btn-primary):not(.ant-btn-dangerous), & .ant-popconfirm-buttons .ant-btn:not(.ant-btn-primary):not(.ant-btn-dangerous), & .ant-popover-buttons .ant-btn-default:not(.ant-btn-dangerous), & .ant-popconfirm-buttons .ant-btn-default:not(.ant-btn-dangerous)': {
      backgroundColor: `${theme.color.semantic.brand.default.subtle} !important`,
      color: `${theme.color.semantic.brand.default.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      fontWeight: `${theme.typography.fontWeight.medium} !important`,
      borderWidth: `${theme.border.width.sm} !important`,
      borderStyle: 'solid !important',
      borderRadius: `${theme.radius.sm} !important`,
      fontSize: `${theme.typography.fontSize.sm} !important`,
      padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
      boxShadow: 'none !important',
      '&:hover:not(:disabled)': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
        color: `${theme.color.semantic.brand.default.secondary} !important`,
        borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      },
      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.brand.default.light} !important`,
        color: `${theme.color.semantic.brand.default.primary} !important`,
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '&:disabled, &.ant-btn-disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.text.soft} !important`,
      },
    },
    // Cancel Button when danger (Secondary Danger)
    '& .ant-popover-buttons .ant-btn-default.ant-btn-dangerous, & .ant-popconfirm-buttons .ant-btn-default.ant-btn-dangerous, & .ant-popover-buttons .ant-btn:not(.ant-btn-primary).ant-btn-dangerous, & .ant-popconfirm-buttons .ant-btn:not(.ant-btn-primary).ant-btn-dangerous': {
      backgroundColor: `${theme.color.semantic.brand.danger.inverse} !important`,
      color: `${theme.color.semantic.brand.danger.primary} !important`,
      borderColor: `${theme.color.semantic.brand.danger.primary} !important`,
      fontWeight: `${theme.typography.fontWeight.medium} !important`,
      borderWidth: `${theme.border.width.sm} !important`,
      borderStyle: 'solid !important',
      borderRadius: `${theme.radius.sm} !important`,
      fontSize: `${theme.typography.fontSize.sm} !important`,
      padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
      boxShadow: 'none !important',
      '&:hover:not(:disabled)': {
        backgroundColor: `${theme.color.semantic.brand.danger.subtle} !important`,
        color: `${theme.color.semantic.brand.danger.primary} !important`,
        borderColor: `${theme.color.semantic.brand.danger.primary} !important`,
      },
      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.brand.danger.light} !important`,
        color: `${theme.color.semantic.brand.danger.primary} !important`,
        borderColor: `${theme.color.semantic.brand.danger.primary} !important`,
      },
      '&:disabled, &.ant-btn-disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.border.soft} !important`,
      },
    },
  },
}));

const Popconfirm: React.FC<PopconfirmProps> = ({ 
  className = '', 
  overlayClassName = '',
  okButtonProps,
  cancelButtonProps,
  ...rest 
}) => {
  const classes = useStyles();
  
  return (
    <AntPopconfirm
      {...rest}
      overlayClassName={`${classes.popconfirm} ${overlayClassName}`.trim()}
      okButtonProps={okButtonProps}
      cancelButtonProps={cancelButtonProps}
    />
  );
};

export default Popconfirm;
